import React, { useEffect } from 'react';
import { CheckboxUI, DebouncedInputUI, StaticDatePickerUI } from '../Interface';
import dayjs from 'dayjs';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view-pro';
import { Box, Typography } from '@mui/material';
import useUsers from '../../context/UsersContext';
import useSaveView from '../../hooks/useSaveView';
import { CalendarType } from '../../config/taskCalendar';
import useParam from '../../context/ParamContext';

const CalendarFilter = ({
  dates,
  resources,
  queryState,
  hasResourceFilter,
  roles,
  calendarTypeState,
  isLgUp,
  isLoading,
}) => {
  const { setCalendarType, calendarType } = calendarTypeState;
  const { user } = useUsers();
  const { params } = useParam();
  const saveView = useSaveView(user);
  const { date, setDate } = dates;
  const { query, setQuery } = queryState;

  const items = query.filter?.filterModel?.items ?? [];
  const USER_FIELD = 'responsiblePersonId';
  const EMPLOYEE_FIELD = 'employeeId';

  useEffect(() => {
    if (!roles.TASK_CALENDAR_SEE_ONLY_OWN || !user?.userId || isLoading) return;

    const currentUser = resources?.user?.find((resourceUser) => resourceUser.userId === user.userId);

    setQuery((prev) => {
      const prevItems = prev.filter?.filterModel?.items ?? [];

      const newItems = prevItems.filter((item) => item.field !== USER_FIELD && item.field !== EMPLOYEE_FIELD);

      newItems.push({
        field: USER_FIELD,
        operator: 'isAnyOf',
        value: [user.userId],
      });

      if (currentUser?.employeeId && calendarType.includes(CalendarType.SIMPLETASK)) {
        newItems.push({
          field: EMPLOYEE_FIELD,
          operator: 'isAnyOf',
          value: [currentUser.employeeId],
        });
      }

      return {
        ...prev,
        filter: {
          ...prev.filter,
          filterModel: {
            ...(prev.filter?.filterModel ?? {}),
            items: newItems,
            logicOperator: 'and',
          },
        },
      };
    });
  }, [roles.TASK_CALENDAR_SEE_ONLY_OWN, user?.userId, resources?.user, calendarType, isLoading]);

  const getFieldItem = (field) => items.find((item) => item.field === field);

  const handleFilterChange = (field, value, operator = 'is') => {
    setQuery((prev) => {
      const prevItems = prev.filter?.filterModel?.items ?? [];
      const newItems = prevItems.filter((i) => i.field !== field);

      if (value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        newItems.push({
          field,
          operator,
          value,
        });
      }

      return {
        ...prev,
        filter: {
          ...prev.filter,
          filterModel: {
            ...(prev.filter?.filterModel ?? {}),
            items: newItems,
            logicOperator: 'and',
          },
        },
      };
    });
  };

  const isChecked = (field, id) => {
    const item = getFieldItem(field);

    if (!item || !Array.isArray(item.value)) return false;
    return item.value.map(String).includes(String(id));
  };

  const handleToggle = (field, id) => {
    if (roles.TASK_CALENDAR_SEE_ONLY_OWN) return;

    const item = getFieldItem(field);
    const current = Array.isArray(item?.value) ? item.value : [];
    const exists = current.map(String).includes(String(id));
    const updated = exists ? current.filter((v) => String(v) !== String(id)) : [...current, id];

    handleFilterChange(field, updated, 'isAnyOf');
  };

  const renderCheckLabel = (field, id, label) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
      <CheckboxUI
        sx={{ m: 0, p: 0 }}
        checked={isChecked(field, id)}
        onChange={() => handleToggle(field, id)}
        disabled={roles.TASK_CALENDAR_SEE_ONLY_OWN || !calendarType.length}
      />
      <Typography>{label}</Typography>
    </Box>
  );

  const handleSelectType = (type) => {
    const next = calendarType.includes(type) ? calendarType.filter((x) => x !== type) : [...calendarType, type];
    setCalendarType(next);
    saveView('taskCalendarFilter', { calendarTypes: next });
  };

  return (
    <div>
      {isLgUp && (
        <>
          <div className="w-full">
            <StaticDatePickerUI
              sx={{
                '& .MuiDatePickerToolbar-root': { display: 'none' },
                '& .MuiDialogActions-root': { display: 'none' },
                ' & .MuiDateCalendar-root': { background: '#f3f4f6' },
              }}
              value={dayjs(date)}
              onChange={(newDate) => {
                setDate((prevState) => ({
                  ...prevState,
                  startDate: newDate ? newDate.startOf('day') : null,
                  endDate: newDate ? newDate.endOf('day') : null,
                }));
              }}
            />
          </div>

          <Box ml={1} mb={1}>
            <DebouncedInputUI
              size="small"
              placeholder="Projekt keresés…"
              debounceMs={800}
              variant="outlined"
              setQuickFilterSearchValue={(newValue) => handleFilterChange('projectName', newValue, 'contains')}
              quickFilterSearchValue={getFieldItem('projectName')?.value}
              disabled={!hasResourceFilter}
            />
          </Box>
        </>
      )}

      <Box ml={1} mb={1}>
        {params.HIDE_TASK_CALENDAR_TASKLIST_CHECKBOX !== 'Y' && (
          <Box>
            <CheckboxUI
              sx={{ m: 0, p: 0 }}
              label="Projekt feladatlista"
              onChange={() => handleSelectType(CalendarType.TASKLIST)}
              checked={calendarType.includes(CalendarType.TASKLIST)}
            />
          </Box>
        )}
        <Box>
          <CheckboxUI
            sx={{ m: 0, p: 0 }}
            label="Feladatok"
            onChange={() => handleSelectType(CalendarType.SIMPLETASK)}
            checked={calendarType.includes(CalendarType.SIMPLETASK)}
          />
        </Box>
      </Box>

      <SimpleTreeView>
        <TreeItem itemId="responsibles-root" label="Felelősök">
          {resources?.user?.map((user) => (
            <TreeItem
              key={`resp-${user.userId}`}
              itemId={`resp-${user.userId}`}
              label={renderCheckLabel(USER_FIELD, user.userId, user.nickName)}
            />
          ))}
        </TreeItem>
      </SimpleTreeView>

      {resources?.employee?.length > 0 && calendarType.includes(CalendarType.SIMPLETASK) && (
        <SimpleTreeView>
          <TreeItem itemId="employees-root" label="Dolgozók">
            {resources?.employee?.map((employee) => (
              <TreeItem
                key={`emp-${employee.id}`}
                itemId={`emp-${employee.id}`}
                label={renderCheckLabel(EMPLOYEE_FIELD, employee.id, employee.name)}
              />
            ))}
          </TreeItem>
        </SimpleTreeView>
      )}
    </div>
  );
};

export default CalendarFilter;
